import { useState, useEffect, useContext } from "react";
import { TgScreen } from "../components/TgScreen";
import { BottomNavContext, Card, MainButton } from "../components/atoms";
import { classifyZecAddress } from "../lib/address-validation";
import { useShieldedKeys } from "../shielded/keys-context";
import { useShieldedBalance } from "../shielded/balance-context";
import { useUnlockedWallet } from "../wallet";
import { Send as SendApi, getSpendables } from "../shielded";
import { recentContacts, recordSendTo } from "../lib/contacts-store";
import { useDevice } from "../lib/device-context";
import { reportProofTiming, reportProofOOM } from "../lib/telemetry";
import { recordTransaction } from "../lib/tx-history";
import { explainError } from "../lib/error-hints";
import { QrScanner, extractAddressFromScan } from "../lib/qr-scanner";

// ZIP-302: a Sapling memo is a fixed 512-byte payload. The user-visible
// memo we sign in must fit in those bytes after UTF-8 encoding (the
// signer pads to 512). Reject anything past that ceiling before we ever
// dispatch to the prover so the failure surfaces with a clear message
// rather than an opaque "memo too long" trap deep inside the WASM.
const MEMO_MAX_BYTES = 512;

// Conservative ZIP-317 fee buffer for the balance gate. Real fee depends
// on logical_actions (t_in + t_out + max(s_in, s_out) + orchard). Pure
// shielded send is usually 10000 zats; we hold back 30000 to leave room
// for an extra output or a small witness rebuild.
const FEE_BUFFER_ZATS = 30_000;

const utf8ByteLength = (s: string): number => new TextEncoder().encode(s).length;

// Locale-tolerant decimal parser. Turkish/EU users type "0,5" with a
// comma — Number.parseFloat silently returns 0 for that, which trips the
// "must be a positive number" check. Treat comma as a decimal separator
// and ignore stray spaces. Thousand separators aren't standardised in
// crypto amounts so we don't try to be clever about them.
const parseAmount = (raw: string): number => {
  const cleaned = raw.replace(/\s+/g, "").replace(",", ".");
  return Number.parseFloat(cleaned);
};

// Format a zat value back to a decimal string for input fields. We
// trim trailing zeros so the Max button doesn't fill in "0.12345000".
const zatsToAmountString = (zats: number): string => {
  if (zats <= 0) return "0";
  const s = (zats / 1e8).toFixed(8);
  return s.replace(/\.?0+$/, "");
};

type Stage =
  | { kind: "form" }
  // fee_zats is the precomputed ZIP-317 fee. If coin selection threw
  // (e.g. INSUFFICIENT_FUNDS) we surface it on the same card via
  // selectError so the user stays one tap from editing — no need to
  // bounce through the global error screen.
  | {
      kind: "confirm";
      amount_zats: number;
      fee_zats: number;
      selectError: string | null;
    }
  | { kind: "sending"; progress: SendApi.SendProgress }
  | { kind: "success"; txid: string; fee: number }
  | { kind: "error"; message: string };

// Telegram haptic feedback. No-op when running outside a Telegram
// WebView. Five kinds map to two underlying calls — impacts for taps
// (light/medium) and notifications for outcomes (success/error/warning).
const haptic = (
  kind: "light" | "medium" | "success" | "error" | "warning",
) => {
  const hf = window.Telegram?.WebApp?.HapticFeedback;
  if (!hf) return;
  if (kind === "success" || kind === "error" || kind === "warning") {
    hf.notificationOccurred?.(kind);
  } else {
    hf.impactOccurred?.(kind);
  }
};

// Open an external URL using Telegram's in-app browser when available
// (keeps the user inside the chat shell), falling back to window.open
// for browser preview / non-Telegram contexts. openLink isn't in our
// TelegramWebApp typedef yet so we reach for it via a narrow cast.
const openExternal = (url: string) => {
  const wa = window.Telegram?.WebApp as
    | (TelegramWebApp & { openLink?: (u: string) => void })
    | undefined;
  if (wa?.openLink) {
    wa.openLink(url);
  } else {
    window.open(url, "_blank");
  }
};

// Middle-ellipsis truncation for a Zcash UA / address. Keeps the first
// 12 and last 8 chars so the user can still sanity-check the prefix and
// suffix at a glance; the full string remains accessible via a tap.
const truncateAddress = (addr: string, head = 12, tail = 8): string => {
  if (addr.length <= head + tail + 1) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
};

interface Props {
  /** Pre-filled when arriving from Contacts or a deep link. */
  initialRecipient?: string;
  initialUsername?: string | null;
  onBack: () => void;
}

export function SendScreen({ initialRecipient = "", initialUsername, onBack }: Props) {
  const wallet = useUnlockedWallet();
  const { state: keysState, keys } = useShieldedKeys();
  const { balance, refresh: refreshBalance } = useShieldedBalance();
  const { assessment } = useDevice();

  const [recipient, setRecipient] = useState(initialRecipient);
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [acceptDeshield, setAcceptDeshield] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [stageStartedAt, setStageStartedAt] = useState<number>(0);
  const [elapsedS, setElapsedS] = useState(0);
  // Tap-to-reveal toggle for the truncated recipient on the confirm card.
  const [showFullRecipient, setShowFullRecipient] = useState(false);
  // Snapshot the recent-contacts list once on mount. The store is a
  // localStorage read so we don't want to call it on every render, and
  // we don't need live updates while the user is filling in the form.
  const [recents] = useState(() => recentContacts());
  // Nav setter from the app shell so the "More →" chip can jump to the
  // Contacts tab. May be null when this screen is rendered outside the
  // BottomNavContext provider (e.g. in a Storybook-style isolation).
  const setTab = useContext(BottomNavContext);

  // Live elapsed-time counter while a send is in-flight. Gives the user
  // confidence the UI isn't frozen during the long decompress stage.
  useEffect(() => {
    if (stage.kind !== "sending") {
      setElapsedS(0);
      return;
    }
    const tick = () =>
      setElapsedS(Math.floor((Date.now() - stageStartedAt) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [stage.kind, stageStartedAt]);

  // Lock the WebView close gesture while the prover is running. A swipe
  // mid-Groth16 throws away ~30 s of CPU work plus the user's intent;
  // Telegram surfaces a confirmation dialog when this is enabled. We
  // unlock on every exit so the form / success / error screens behave
  // normally.
  useEffect(() => {
    if (stage.kind !== "sending") return;
    const wa = window.Telegram?.WebApp;
    wa?.enableClosingConfirmation?.();
    return () => {
      wa?.disableClosingConfirmation?.();
    };
  }, [stage.kind]);

  useEffect(() => {
    if (initialRecipient) setRecipient(initialRecipient);
  }, [initialRecipient]);

  // A new recipient toggles the de-shield acknowledgement back off so
  // the user can't carry consent across to a different t-addr by accident.
  useEffect(() => {
    setAcceptDeshield(false);
  }, [recipient]);

  const recipientKind = classifyZecAddress(recipient);
  const amountZec = parseAmount(amount);
  const amountValid = Number.isFinite(amountZec) && amountZec > 0;
  const amountZats = amountValid ? Math.round(amountZec * 1e8) : 0;
  const spendable = balance.netBalanceZats;
  const overBalance =
    amountValid && amountZats + FEE_BUFFER_ZATS > spendable;
  const memoBytes = utf8ByteLength(memo);
  const memoTooLong = memoBytes > MEMO_MAX_BYTES;
  const isTransparentRecipient = recipientKind === "transparent";
  const needsDeshieldAck = isTransparentRecipient && !acceptDeshield;

  const formValid =
    recipientKind !== "invalid" &&
    amountValid &&
    !overBalance &&
    !memoTooLong &&
    !needsDeshieldAck &&
    stage.kind === "form";

  // Step 1: from form → confirm. Precomputes the ZIP-317 fee using the
  // same coin selector the signer will run later; if there aren't enough
  // notes to cover amount+fee, surface that here rather than at proof
  // time. We pre-validate but stop short of building the transaction —
  // the user still has to tap "Confirm & Send". The hook's ShieldedBalance
  // type only exposes a count, not the notes themselves, so we read the
  // persisted spendables snapshot directly.
  const onSend = async () => {
    if (!wallet || !keys) return;
    if (!formValid) return;
    try {
      const { notes } = await getSpendables(keys.ufvk);
      const picked = SendApi.selectCoins({
        notes,
        amount_zats: amountZats,
        num_outputs: 1,
        num_transparent_outputs: isTransparentRecipient ? 1 : 0,
      });
      setStage({
        kind: "confirm",
        amount_zats: amountZats,
        fee_zats: picked.fee_zats,
        selectError: null,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setStage({
        kind: "confirm",
        amount_zats: amountZats,
        fee_zats: 0,
        selectError: message,
      });
    }
  };

  // Step 2: from confirm → broadcasting. This is the body of the old
  // onSend — runs the prover, broadcasts, records history.
  const onConfirmAndSend = async () => {
    if (!wallet || !keys) return;
    if (stage.kind !== "confirm") return;

    haptic("medium");
    setStage({
      kind: "sending",
      progress: { stage: "syncing-witnesses" },
    });
    setStageStartedAt(Date.now());
    const t0 = performance.now();
    try {
      const res = await SendApi.sendShielded(
        {
          ufvk: keys.ufvk,
          seed_phrase: wallet.phrase,
          account: keys.account,
          recipient,
          amount_zats: amountZats,
          memo: memo || undefined,
        },
        (p) => setStage({ kind: "sending", progress: p }),
      );
      const total_ms = Math.round(performance.now() - t0);
      // Bump the send count on the contact (if it was one).
      recordSendTo(recipient);
      // Append to local outgoing-tx history.
      recordTransaction({
        kind: "send",
        txid: res.txid,
        amount_zats: amountZats,
        fee_zats: res.fee_zats,
        recipient,
        recipient_username: initialUsername ?? null,
        swap_to_asset: null,
        swap_to_address: null,
        memo: memo || null,
      });
      // Fire-and-forget telemetry (errors swallowed inside the helper).
      reportProofTiming({
        kind: "send",
        device_class: assessment?.class ?? "ok",
        total_ms,
        proving_ms: null,
        decompress_ms: null,
        spend_count: 0,
        output_count: 1,
      });
      // Re-poll balance now that a note has been spent — the UI elsewhere
      // would otherwise show the pre-send total until the next interval.
      void refreshBalance();
      setStage({ kind: "success", txid: res.txid, fee: res.fee_zats });
      haptic("success");
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // Classify likely OOMs from the worker — wbindgen surfaces RawTrap
      // / "out of memory" / "RangeError" in different ways depending on
      // the engine; match all the common shapes.
      const looksLikeOom = /out of memory|RangeError|wasm.*memory/i.test(message);
      if (looksLikeOom) {
        reportProofOOM({
          kind: "send",
          device_class: assessment?.class ?? "ok",
          message,
        });
      }
      setStage({ kind: "error", message });
      haptic("error");
    }
  };

  const isBusy = stage.kind === "sending";

  // Render: form / progress / success / error
  if (stage.kind === "success") {
    return (
      <TgScreen title="Sent" onClose={onBack}>
        <div style={{ padding: "16px 16px 24px" }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
              <span className="bounce-in">✓</span> Shielded send broadcast
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--hint)",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Tx id
            </div>
            <div style={{ fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>
              {stage.txid}
            </div>
            <div style={{ marginTop: 14, color: "var(--hint)", fontSize: 13 }}>
              Fee: {(stage.fee / 1e8).toFixed(8)} ZEC
            </div>
          </Card>
          <button
            onClick={() =>
              openExternal(`https://blockchair.com/zcash/transaction/${stage.txid}`)
            }
            style={{
              marginTop: 16,
              width: "100%",
              padding: "12px",
              background: "transparent",
              color: "var(--link, #2c8acb)",
              border: "1px solid var(--link, #2c8acb)",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
          >
            View Zcash tx ↗
          </button>
          <button
            onClick={onBack}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "12px",
              background: "var(--tg-theme-button-color, #2c8acb)",
              color: "var(--tg-theme-button-text-color, white)",
              border: "none",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </TgScreen>
    );
  }

  if (stage.kind === "error") {
    const hint = explainError(stage.message);
    return (
      <TgScreen title="Send failed" onClose={onBack}>
        <div style={{ padding: "16px 16px 24px" }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--destructive, #d33)" }}>
              {hint.headline}
            </div>
            {hint.suggestion && (
              <div style={{ marginTop: 8, color: "var(--text-soft)", fontSize: 13, lineHeight: 1.45 }}>
                {hint.suggestion}
              </div>
            )}
            <details style={{ marginTop: 14 }}>
              <summary
                style={{
                  fontSize: 11,
                  color: "var(--hint)",
                  cursor: "pointer",
                  userSelect: "none",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Technical details
              </summary>
              <div
                style={{
                  marginTop: 6,
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: "var(--hint)",
                  wordBreak: "break-word",
                }}
              >
                {stage.message}
              </div>
            </details>
          </Card>
          <button
            onClick={() => setStage({ kind: "form" })}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "12px",
              background: "var(--tg-theme-button-color, #2c8acb)",
              color: "var(--tg-theme-button-text-color, white)",
              border: "none",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </TgScreen>
    );
  }

  if (stage.kind === "confirm") {
    const insufficient = stage.selectError != null;
    const privacyBadge = isTransparentRecipient
      ? { text: "⚠ De-shielding to t1", color: "var(--warning, #e0a035)" }
      : { text: "Private — shielded to shielded", color: "var(--success, #34C759)" };
    return (
      <TgScreen title="Review send" onClose={onBack}>
        <div style={{ padding: "16px 16px 24px" }}>
          <Card style={{ padding: 18, marginBottom: 12 }}>
            {/* Pulsing privacy badge — green for fully-shielded sends,
                amber when the destination forces de-shielding. The keyframes
                live in styles.css; if they're missing the badge falls back
                to a static pill, which is fine. */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                marginBottom: 14,
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                color: privacyBadge.color,
                border: `1px solid ${privacyBadge.color}`,
                animation: "tgPulse 2s ease-in-out infinite",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: privacyBadge.color,
                  boxShadow: `0 0 6px ${privacyBadge.color}`,
                }}
              />
              {privacyBadge.text}
            </div>

            <Section label="To">
              <button
                type="button"
                onClick={() => setShowFullRecipient((v) => !v)}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "var(--text)",
                  fontFamily: "monospace",
                  fontSize: showFullRecipient ? 11 : 13,
                  lineHeight: 1.4,
                  textAlign: "left",
                  wordBreak: "break-all",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
                title={showFullRecipient ? "Tap to collapse" : "Tap to show full address"}
              >
                {showFullRecipient ? recipient : truncateAddress(recipient)}
              </button>
            </Section>

            <Section label="Amount">
              {(stage.amount_zats / 1e8).toFixed(8)} ZEC
            </Section>

            <Section label="Fee">
              {insufficient ? (
                <span style={{ color: "var(--destructive, #d33)", fontSize: 12 }}>
                  {stage.selectError}
                </span>
              ) : (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {(stage.fee_zats / 1e8).toFixed(8)} ZEC
                </span>
              )}
            </Section>

            {memo && (
              <Section label="Memo">
                <span style={{ fontSize: 13, wordBreak: "break-word" }}>{memo}</span>
              </Section>
            )}
          </Card>

          <MainButton
            text="Confirm & Send"
            onClick={onConfirmAndSend}
            disabled={insufficient || !keys}
          />
          <button
            onClick={() => {
              // Back to form preserving every field — we never cleared
              // them on the way in.
              setStage({ kind: "form" });
              setShowFullRecipient(false);
            }}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "12px",
              background: "transparent",
              color: "var(--hint)",
              border: "1px solid var(--hairline, #2c333a)",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
          >
            Edit
          </button>
        </div>
      </TgScreen>
    );
  }

  return (
    <TgScreen title="Send shielded ZEC" bottomNav="send">
      <div style={{ padding: "12px 16px 24px" }}>
        {spendable === 0 && (
          <Card
            style={{
              padding: 12,
              marginBottom: 12,
              background: "rgba(255, 200, 80, 0.08)",
              border: "1px solid rgba(255, 200, 80, 0.25)",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <div style={{ fontSize: 18, lineHeight: 1 }}>💡</div>
            <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.5 }}>
              <b>You have no shielded ZEC yet.</b> Send some to the address on
              the Receive tab first — once it confirms, the Send button will
              unlock automatically.
            </div>
          </Card>
        )}
        {initialUsername && (
          <div
            style={{
              fontSize: 13,
              color: "var(--hint)",
              marginBottom: 8,
            }}
          >
            To <b style={{ color: "var(--text)" }}>@{initialUsername}</b>
          </div>
        )}
        {/* Recent contacts chip row. Only shown while the recipient field
            is empty — once the user picks (or pastes) anything, the chips
            collapse to give the address full visual weight. Tapping a chip
            fills the field with the canonical UA; the "More →" chip drops
            the user on the Contacts tab via BottomNavContext. */}
        {!recipient && recents.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Label>Recent</Label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              {recents.slice(0, 3).map((c) => {
                const display = c.username
                  ? `@${c.username}`
                  : `${c.unifiedAddress.slice(0, 6)}…`;
                return (
                  <button
                    key={c.unifiedAddress}
                    type="button"
                    onClick={() => {
                      setRecipient(c.unifiedAddress);
                      haptic("light");
                    }}
                    style={{
                      background: "var(--bg-2)",
                      border: "1px solid var(--hairline, #2c333a)",
                      color: "var(--text)",
                      padding: "6px 12px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      fontFamily: "var(--font-text)",
                    }}
                  >
                    {display}
                  </button>
                );
              })}
              {recents.length > 3 && setTab && (
                <button
                  type="button"
                  onClick={() => setTab("contacts")}
                  style={{
                    background: "var(--bg-2)",
                    border: "1px solid var(--hairline, #2c333a)",
                    color: "var(--link, #2c8acb)",
                    padding: "6px 12px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "var(--font-text)",
                  }}
                >
                  More →
                </button>
              )}
            </div>
          </div>
        )}
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <Label>Recipient</Label>
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setScanOpen(true);
              }}
              disabled={isBusy}
              aria-label="Scan QR"
              style={{
                all: "unset",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "4px 10px",
                background: "var(--bg-3, #1F242D)",
                color: "var(--link, #6CB0FF)",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                cursor: isBusy ? "default" : "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="3" y="3" width="6" height="6" rx="1" />
                <rect x="15" y="3" width="6" height="6" rx="1" />
                <rect x="3" y="15" width="6" height="6" rx="1" />
                <path d="M15 15h2v2h-2zM19 15h2v2h-2zM15 19h2v2h-2zM19 19h2v2h-2z" />
              </svg>
              Scan
            </button>
          </div>
          <Input
            value={recipient}
            onChange={setRecipient}
            placeholder="u1… or zs1… or t1…"
            disabled={isBusy}
          />
          {recipient && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color:
                  recipientKind === "invalid"
                    ? "var(--destructive, #d33)"
                    : isTransparentRecipient
                      ? "var(--warning, #e0a035)"
                      : "var(--hint)",
              }}
            >
              {recipientKind === "invalid"
                ? "Not a valid Zcash address"
                : recipientKind === "unified"
                  ? "Private — shielded to shielded"
                  : recipientKind === "sapling-legacy"
                    ? "Private (legacy Sapling)"
                    : "⚠ Transparent — this de-shields your ZEC"}
            </div>
          )}
        </Card>

        {/* Empty-state nudge: only when both the field is empty AND there
            are no recent chips to choose from. With chips present the row
            above is sufficient guidance. */}
        {!recipient && recents.length === 0 && (
          <div
            style={{
              background: "var(--bg-2)",
              border: "1px solid var(--hairline, #2c333a)",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 12,
              color: "var(--text-soft)",
              marginBottom: 12,
            }}
          >
            💡  Paste a u1… address, type @username, or tap a recent contact above.
          </div>
        )}

        {isTransparentRecipient && (
          <Card
            style={{
              padding: 12,
              marginBottom: 12,
              background: "rgba(255, 200, 80, 0.08)",
              border: "1px solid rgba(255, 200, 80, 0.25)",
            }}
          >
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1.5,
                color: "var(--text-soft)",
              }}
            >
              <input
                type="checkbox"
                checked={acceptDeshield}
                onChange={(e) => setAcceptDeshield(e.target.checked)}
                disabled={isBusy}
                style={{ marginTop: 2, accentColor: "#e0a035" }}
              />
              <span>
                I understand sending to a <b>t1</b> address makes the amount,
                sender note, and recipient public on-chain.
              </span>
            </label>
          </Card>
        )}

        <Card style={{ padding: 14, marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 6,
              gap: 8,
            }}
          >
            <Label>Amount (ZEC)</Label>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 11, color: "var(--hint)" }}>
                Balance: {(spendable / 1e8).toFixed(8)}
              </span>
              <button
                type="button"
                onClick={() => {
                  // Max sendable = balance minus the conservative ZIP-317
                  // fee buffer. If the buffer eats the whole balance we
                  // fill 0 — the diagnostic below explains why.
                  const max = Math.max(0, spendable - FEE_BUFFER_ZATS);
                  setAmount(zatsToAmountString(max));
                  haptic("light");
                }}
                disabled={isBusy}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  background: "transparent",
                  color: "var(--link)",
                  border: "1px solid currentColor",
                  borderRadius: 999,
                  cursor: isBusy ? "wait" : "pointer",
                  fontFamily: "var(--font-text)",
                }}
              >
                MAX
              </button>
            </div>
          </div>
          <Input
            value={amount}
            onChange={setAmount}
            placeholder="0.001"
            disabled={isBusy}
            inputMode="decimal"
          />
          {amountValid && overBalance && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "var(--destructive, #d33)",
              }}
            >
              Amount + fee exceeds your spendable balance.
            </div>
          )}
        </Card>

        <Card style={{ padding: 14, marginBottom: 12 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <Label>Memo (optional)</Label>
            <span
              style={{
                fontSize: 11,
                color: memoTooLong
                  ? "var(--destructive, #d33)"
                  : "var(--hint)",
              }}
            >
              {memoBytes}/{MEMO_MAX_BYTES}
            </span>
          </div>
          <Input
            value={memo}
            onChange={setMemo}
            placeholder="Visible only to recipient"
            disabled={isBusy}
          />
          {memoTooLong && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "var(--destructive, #d33)",
              }}
            >
              Memo is over the 512-byte ZIP-302 limit. Shorten it.
            </div>
          )}
        </Card>

        {stage.kind === "sending" && (
          <Card
            style={{
              padding: 14,
              marginBottom: 12,
              background: "rgba(46, 187, 96, 0.06)",
              border: "1px solid rgba(46, 187, 96, 0.18)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 4,
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {progressLabel(stage.progress.stage)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--hint)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtElapsed(elapsedS)}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>
              {stage.progress.params?.label ?? progressHint(stage.progress.stage)}
            </div>
            {stage.progress.params && (
              <ProgressBar pct={stage.progress.params.pct} />
            )}
            {elapsedS >= 45 && stage.progress.stage === "loading-params" && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: "var(--warning, #FFB020)",
                  lineHeight: 1.5,
                }}
              >
                Decompression is slower than usual on this device. Hang on — or
                tap Cancel below and try again from the form.
              </div>
            )}
            <button
              type="button"
              onClick={() => setStage({ kind: "form" })}
              style={{
                marginTop: 12,
                width: "100%",
                background: "transparent",
                color: "var(--hint)",
                border: "1px solid var(--hairline, #2c333a)",
                borderRadius: 10,
                padding: "8px",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              Cancel
            </button>
          </Card>
        )}

        {keysState.kind === "deriving" && (
          <div
            style={{
              fontSize: 12,
              color: "var(--hint)",
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            Deriving shielded keys…
          </div>
        )}

        {/* Surface the first blocking reason so the user knows what to fix. */}
        {!isBusy && (!formValid || !keys) && (
          <div
            style={{
              fontSize: 12,
              color: "var(--hint)",
              textAlign: "center",
              marginBottom: 8,
              lineHeight: 1.5,
            }}
          >
            {disabledReason({
              hasKeys: !!keys,
              keysState: keysState.kind,
              recipient,
              recipientKind,
              amount,
              amountValid,
              overBalance,
              memoTooLong,
              needsDeshieldAck,
              isTransparentRecipient,
              spendable,
              feeBuffer: FEE_BUFFER_ZATS,
            })}
          </div>
        )}

        <MainButton
          text={isBusy ? "Working…" : "Send"}
          onClick={onSend}
          disabled={!formValid || !keys || isBusy}
        />
      </div>
      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onDecode={(text) => {
          const addr = extractAddressFromScan(text);
          setRecipient(addr);
          setScanOpen(false);
        }}
      />
    </TgScreen>
  );
}

interface DisabledReasonArgs {
  hasKeys: boolean;
  keysState: string;
  recipient: string;
  recipientKind: ReturnType<typeof classifyZecAddress>;
  amount: string;
  amountValid: boolean;
  overBalance: boolean;
  memoTooLong: boolean;
  needsDeshieldAck: boolean;
  isTransparentRecipient: boolean;
  spendable: number;
  feeBuffer: number;
}

function disabledReason(a: DisabledReasonArgs): string {
  if (!a.hasKeys) {
    if (a.keysState === "deriving") return "Waiting on shielded keys to derive…";
    if (a.keysState === "error") return "Shielded keys failed to load. Re-open the wallet.";
    return "Shielded keys aren't ready yet.";
  }
  if (!a.recipient.trim()) return "Enter or paste a Zcash address to send to.";
  if (a.recipientKind === "invalid")
    return "Recipient isn't a valid Zcash address (expected u1…, zs1…, or t1…).";
  // Balance below the fee floor — diagnose specifically so the user
  // doesn't waste time wondering why "0" or any tiny amount won't go.
  if (a.spendable > 0 && a.spendable <= a.feeBuffer) {
    return `Your balance (${(a.spendable / 1e8).toFixed(8)} ZEC) is below the network fee floor (~${(a.feeBuffer / 1e8).toFixed(4)} ZEC). Receive more ZEC first.`;
  }
  if (!a.amount.trim()) return "Enter an amount to send.";
  if (!a.amountValid)
    return 'Amount must be a positive number. Use "." or "," as the decimal separator.';
  if (a.overBalance)
    return "Amount + fee exceeds your spendable balance — try a smaller amount or tap MAX.";
  if (a.memoTooLong) return "Memo is over the 512-byte ZIP-302 limit.";
  if (a.needsDeshieldAck && a.isTransparentRecipient)
    return "Tick the de-shield acknowledgement above to send to a t1 address.";
  return "";
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: "var(--hint)",
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

// Stacked label + value row, used on the confirm card. Mirrors the
// helper in Swap.tsx so the two review screens look identical.
function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1,
          color: "var(--hint)",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>
        {children}
      </div>
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  disabled,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: "text" | "decimal";
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      inputMode={inputMode}
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        outline: "none",
        fontSize: 15,
        fontFamily: "var(--font-text)",
        color: "var(--text)",
        padding: 0,
      }}
    />
  );
}

function fmtElapsed(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

function ProgressBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      style={{
        marginTop: 10,
        height: 6,
        background: "var(--bg-3, #1F242D)",
        borderRadius: 999,
        overflow: "hidden",
        position: "relative",
      }}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        style={{
          width: `${clamped}%`,
          height: "100%",
          background:
            "linear-gradient(90deg, var(--success, #34C759), var(--accent, #F0B400))",
          borderRadius: 999,
          transition: "width 200ms ease-out",
          boxShadow: "0 0 8px rgba(52,199,89,0.35)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 4,
          top: -16,
          fontSize: 10,
          color: "var(--hint)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clamped}%
      </div>
    </div>
  );
}

function progressLabel(stage: SendApi.SendProgress["stage"]): string {
  switch (stage) {
    case "syncing-witnesses":
      return "Syncing witnesses…";
    case "loading-params":
      return "Loading proving keys…";
    case "selecting-coins":
      return "Selecting notes…";
    case "proving":
      return "Generating zero-knowledge proofs…";
    case "broadcasting":
      return "Broadcasting…";
    case "done":
      return "Done";
  }
}

function progressHint(stage: SendApi.SendProgress["stage"]): string {
  switch (stage) {
    case "syncing-witnesses":
      return "Refreshing anchor and witness paths against the latest block.";
    case "loading-params":
      return "First send only: this can take 5-30 seconds while Sapling params decompress.";
    case "selecting-coins":
      return "Picking the smallest set of notes that cover the amount.";
    case "proving":
      return "Groth16 proofs run on your device — don't close this tab.";
    case "broadcasting":
      return "Relaying the signed transaction to lightwalletd.";
    case "done":
      return "";
  }
}
